import React, { createElement } from "react";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import sanitizeHtml from "sanitize-html";
import className from './style.module.scss';

// This component addresses HTML SANITIZATION and FORMATTING for strings
    // It also assists in managing TRANSLATIONS with react-intl formatMessage
    // Custom tags can be established, imported and supported here

// Defined formatting categories
    // When adding new categories, be sure to add it to the "oneOf" list in the PropTypes

const PLAIN_TEXT = [];

const BUTTON_TEXT = [
    "sup", "sup",
];

const LIGHT_INLINE_TEXT = [
    "b", "strong", "i", "em", "s", "u", "sub", "sup", 
];

const INLINE_TEXT = [
    ...LIGHT_INLINE_TEXT,
    "a", "abbr", "wbr",
    "cite", "dfn", "details", "summary", "kbd", "mark", "q", "var", 

];

const LIGHT_BLOCK_TEXT = [
    ...LIGHT_INLINE_TEXT,
    "h1", "h2", "h3", "h4", "h5", "h6",
    "p", "br", "hr",
];

const TABLE_TEXT = [
    "table", "caption", "thead", "tbody", "tfoot", "tr", "th", "td",
];

const LIST_TEXT = [
    "ul", "ol", "li", "dl", "dt", "dd",
];

const BLOCK_TEXT = [
    ...INLINE_TEXT,
    ...LIGHT_BLOCK_TEXT,
    ...TABLE_TEXT,
    ...LIST_TEXT,
];

const CUSTOM = [
    "highlight", 
];

const ALL = [
    ...BLOCK_TEXT,
    ...CUSTOM,
];

const FORMAT_CATEGORIES = {
    "PLAIN_TEXT": PLAIN_TEXT,
    "BUTTON_TEXT": BUTTON_TEXT,
    "LIGHT_INLINE_TEXT": LIGHT_INLINE_TEXT,
    "INLINE_TEXT": INLINE_TEXT,
    "LIGHT_BLOCK_TEXT": LIGHT_BLOCK_TEXT,
    "TABLE_TEXT": TABLE_TEXT,
    "LIST_TEXT": LIST_TEXT,
    "BLOCK_TEXT": BLOCK_TEXT,
    "ALL": ALL,
}


export function FormattedMessage(props) {

    const intl = useIntl();

    const formatBasicTag = (tag, chunks) => {
        if ( getAllowedTags().includes(tag) ) {
            return (
                <CreateBasicElement tag={tag} dir="auto" className={className[tag]}>{chunks}</CreateBasicElement>
            );
        }
        else return chunks;
    };

    const CreateBasicElement = ({ tag, children, ...props }) => {
        return createElement(tag, props, children);
    };

    const formatTagWithAttributes = (tag, chunks, attributeArray) => {
        if( getAllowedTags().includes(tag) ){
            // NOTE: since there are no unique identifiers for chunks other than the text, if the text is identical for multiple of the same tags, the atributes will be orriden with one occurance
            let attributes = getAttributes(tag, attributeArray, chunks);

            switch (tag) {

                case "a":
                    return (
                        <a className={className.internalLink} href={attributes[tag]["url"] || null} target="_blank" dir="auto">
                            {chunks}
                        </a>
                    );

                case "abbr":
                    return (
                        <abbr className={className.abbr} title={attributes[tag]["title"] || null} dir="auto">
                            {chunks}
                        </abbr>
                    );

                case "highlight": // Example of custom tag / element
                    return (
                        <span className={className.highlight} dir="auto"
                            style={{backgroundColor: attributes[tag]["color"] || null}}
                        >
                            {chunks}
                        </span>
                    );

                default: return chunks;
            }
        } else return chunks;
    };

    const getAttributes = (tag, attributeKeys, chunks) => {
        // since there are no unique identifiers for chunks (other than the text), if the text is the same for multiple tags of the same name / type, then all of the attributes of the same name will be overriden by one occurrence 

        let attributes = {};

        // Use regex to extract substrings / matches from the original string that fall between the tag start and end while the content within the tag is equal to the chunk
        let regex = new RegExp(`<${tag} [^>]*>${chunks}<\\/${tag}>`, 'g');
        let matches = props.string.match(regex);
        matches.map((match) => {

            attributes[tag] = {};

            attributeKeys.map((key) => {
                // Use regex to extract variable (keyMatch[1]) from the previouse match (chunkMatch) that fall between the attribute start and end (attribute='variable')
                regex = new RegExp(key + "='(.*?)'", "g");
                let keyMatch;

                while ((keyMatch = regex.exec(match)) !== null) {
                    // Assign the attribute variable / substring to the attribute data for the respective chunks
                    attributes[tag][key] = keyMatch[1];
                }
            })

        })
        return attributes;
    };

    const getAllowedTags = () => {

        // allowed tags based on category
        let allowedTags = Array.isArray(FORMAT_CATEGORIES[props.formatCategory]) ? FORMAT_CATEGORIES[props.formatCategory] : [];

        // additional allowed tags based on custom array provided
        let additionalTags = Array.isArray(props.allowAdditionalTags) ? props.allowAdditionalTags : [];
        allowedTags = [ ...allowedTags, ...additionalTags ]

        // tags to excluded based on custom array provided
        let excludeTags = Array.isArray(props.excludeTags) ? props.excludeTags : [];

        // filter any excluded tags
        excludeTags.map((tag)=>{
            allowedTags = allowedTags.filter(item => item !== tag);
        })

        return allowedTags;
    };

    return (
        intl.formatMessage({
            id: props.string,
            defaultMessage: sanitizeHtml(props.fallbackString || props.string, {
                allowedTags: getAllowedTags()
            })
        }, {
            // All supported tags are listed below
            // further filtering is applied in the "formatTag" function, by formatCategory
            p: (chunks) => (formatBasicTag("p", chunks)),
            h1: (chunks) => (formatBasicTag("h1", chunks)),
            h2: (chunks) => (formatBasicTag("h2", chunks)),
            h3: (chunks) => (formatBasicTag("h3", chunks)),
            h4: (chunks) => (formatBasicTag("h4", chunks)),
            h5: (chunks) => (formatBasicTag("h5", chunks)),
            h6: (chunks) => (formatBasicTag("h6", chunks)),
            b: (chunks) => (formatBasicTag("b", chunks)),
            strong: (chunks) => (formatBasicTag("strong", chunks)),
            i: (chunks) => (formatBasicTag("i", chunks)),
            em: (chunks) => (formatBasicTag("em", chunks)),
            sup: (chunks) => (formatBasicTag("sup", chunks)),
            sub: (chunks) => (formatBasicTag("sub", chunks)),
            s: (chunks) => (formatBasicTag("s", chunks)),
            u: (chunks) => (formatBasicTag("u", chunks)),
            ul: (chunks) => (formatBasicTag("ul", chunks)),
            ol: (chunks) => (formatBasicTag("ul", chunks)),
            li: (chunks) => (formatBasicTag("li", chunks)),
            dl: (chunks) => (formatBasicTag("dl", chunks)),
            dt: (chunks) => (formatBasicTag("dt", chunks)),
            dd: (chunks) => (formatBasicTag("dd", chunks)),
            table: (chunks) => (formatBasicTag("table", chunks)),
            thead: (chunks) => (formatBasicTag("thead", chunks)),
            tbody: (chunks) => (formatBasicTag("tbody", chunks)),
            tr: (chunks) => (formatBasicTag("tr", chunks)),
            th: (chunks) => (formatBasicTag("th", chunks)),
            td: (chunks) => (formatBasicTag("td", chunks)),
            br: <br />,
            hr: <hr />,
            wbr: <wbr />,

            // Tag with attributes
            a: (chunks) => (formatTagWithAttributes("a", chunks, ["url"])),
            abbr: (chunks) => (formatTagWithAttributes("abbr", chunks, ["title"])),
            // Example of custom tag / element
            highlight: (chunks) => (formatTagWithAttributes("highlight", chunks, ["color"])),

            // additional / custom values
            // "This is a dynamic {name}" -- values: {"name": "Andrew"}
            // Other potential formatting options: https://formatjs.io/docs/icu-messageformat-parser
            ...props.values,
        })
    );
}

FormattedMessage.propTypes = {
    string: PropTypes.string.isRequired,
    fallbackString: PropTypes.string,
    formatCategory: PropTypes.oneOf([
        "PLAIN_TEXT",
        "LIGHT_INLINE_TEXT", "INLINE_TEXT",
        "LIGHT_BLOCK_TEXT", "BLOCK_TEXT",
        "BUTTON_TEXT", "TABLE_TEXT", "LIST_TEXT",
        "ALL",
    ]),
    allowAdditionalTags: PropTypes.arrayOf(
        PropTypes.oneOf([
            ...FORMAT_CATEGORIES.ALL,
        ])
    ),
    excludeTags: PropTypes.arrayOf(
        PropTypes.oneOf([
            ...FORMAT_CATEGORIES.ALL,
        ])
    ),
    values: PropTypes.object,
};

FormattedMessage.defaultProps = {
    formatCategory: "PLAIN_TEXT",
    allowAdditionalTags: [],
    excludeTags: [],
    values: {},
    fallbackString: null,
};
